import React from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import RefreshIcon from '@atlaskit/icon/core/refresh';
import { Flex, Grid, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { isInsufficientContextError } from '../../../common/isInsufficientContextError';
import { useAudioPlayerStreamingContext } from '../../context/AudioPlayerStreamingProvider';
import { PlayerErrorIllustration } from '../assets/PlayerErrorIllustration';

type AudioPlayerErrorProps = {
	error: Error;
};

const i18n = defineMessages({
	errorHeading: {
		id: 'ai-audio.player.error-heading',
		defaultMessage: "Audio player isn't working",
		description: 'Error heading shown for the audio player error state',
	},
	insufficientContextErrorHeading: {
		id: 'ai-audio.player.insufficient-context-error-heading',
		defaultMessage: 'Not enough info to create a briefing',
		description: 'Error heading shown when there is not enough content to generate audio',
	},
	errorMessage: {
		id: 'ai-audio.player.error-message',
		defaultMessage: 'Try again',
		description: 'Error message shown for the audio player error state',
	},
	refetchAudioButtonLabel: {
		id: 'ai-audio.player.refetch-audio-button-label',
		defaultMessage: 'Load audio again',
		description: 'Label for the refetch button shown on the audio player error state',
	},
});

const styles = cssMap({
	container: {
		height: '78px',
		width: '348px',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		borderRadius: token('radius.xlarge'),
		boxShadow: token('elevation.shadow.overflow'),
	},
	grid: {
		gridTemplateColumns: '48px 1fr 24px',
		flex: 1,
		width: '100%',
	},
	errorIllustration: {
		paddingInline: token('space.100'),
		paddingBlock: token('space.050'),
	},
});

export const AudioPlayerError = ({ error }: AudioPlayerErrorProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const { fetchAudio } = useAudioPlayerStreamingContext();

	const hasInsufficientContext = isInsufficientContextError(error);

	const handleRetry = () => {
		fetchAudio();
	};

	return (
		<Flex justifyContent="center" alignItems="center" gap="space.050" xcss={styles.container}>
			<Grid columnGap="space.100" xcss={styles.grid} alignItems="center">
				<Flex alignItems="center" justifyContent="center" xcss={styles.errorIllustration}>
					<PlayerErrorIllustration />
				</Flex>

				<Stack alignBlock="center" space="space.050">
					<Heading size="xsmall">
						{formatMessage(
							hasInsufficientContext ? i18n.insufficientContextErrorHeading : i18n.errorHeading,
						)}
					</Heading>
					{!hasInsufficientContext && (
						<Text size="small" color="color.text.subtlest">
							{formatMessage(i18n.errorMessage)}
						</Text>
					)}
				</Stack>

				{!hasInsufficientContext && (
					<IconButton
						icon={RefreshIcon}
						appearance="subtle"
						label={formatMessage(i18n.refetchAudioButtonLabel)}
						onClick={handleRetry}
					/>
				)}
			</Grid>
		</Flex>
	);
};
